import { ApiError } from '@lib/network/api';

/**
 * What can go wrong on the media-comment routes, and what to tell the reader about it.
 *
 * The codes live here rather than beside the store because nothing in the store branches on
 * them — it throws, and the surface that asked for the write decides what to say. Keeping the
 * vocabulary in one place is what stops the panel and the composer explaining the same refusal
 * two different ways.
 *
 * The API's own `message` is a diagnostic written for us, not copy written for the reader
 * ("a media holds at most 100 comments"), so it never reaches the screen.
 */
export const MEDIA_COMMENT_ERROR = {
  commentLimitReached: 'media.comment.limit_reached',
  replyLimitReached: 'media.comment.reply_limit_reached',
  notFound: 'media.comment.not_found',
  replyNotFound: 'media.comment.reply_not_found',
  forbidden: 'media.comment.forbidden',
  replyForbidden: 'media.comment.reply_forbidden',
  /** The write lost a race and the state that refused it is already gone: repeating it succeeds. */
  conflict: 'media.comment.conflict',
} as const;

/**
 * One sentence explaining a failed comment write, for the toast's description.
 *
 * Every branch says what to do next, because all of these are recoverable: the two caps by
 * deleting something, a race by repeating the action, and a comment that has gone by reopening
 * the media. The fallback covers everything else, including no network at all — which is why
 * it suggests trying again rather than naming a cause it cannot know.
 */
export function describeMediaCommentError(error: unknown): string {
  const code = error instanceof ApiError ? error.code : null;
  switch (code) {
    case MEDIA_COMMENT_ERROR.commentLimitReached:
      return 'This media is holding as many comments as it can. Delete one to make room.';
    case MEDIA_COMMENT_ERROR.replyLimitReached:
      return 'This comment is holding as many replies as it can.';
    case MEDIA_COMMENT_ERROR.notFound:
    case MEDIA_COMMENT_ERROR.replyNotFound:
      return 'It has been deleted. Reopen this media to see the current comments.';
    case MEDIA_COMMENT_ERROR.forbidden:
    case MEDIA_COMMENT_ERROR.replyForbidden:
      return 'Only the person who wrote it can change it.';
    case MEDIA_COMMENT_ERROR.conflict:
      return 'Something else changed at the same time. Try that again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
